import { useState, useCallback, useMemo } from "react";
import { useChats, useFileUpload } from "../../hooks";
import { useMessageState, useProgressState, useConnectionState, useChatState, useToastState } from "../../store/useAppStore";
import { ResultsModal } from "./ResultsModal";

function ChatPageV2() {
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [activeFilters, setActiveFilters] = useState({ status: 'all' });

  // Hooks de estado
  const { isLoading } = useConnectionState();
  const {
    filteredChats,
    selectedChats,
    searchQuery,
    areAllFilteredSelected,
    handleSearch,
    handleChatSelection,
    handleToggleAllFiltered,
    handleClearSelection,
    handleRefresh,
    getGroupName,
    allChats
  } = useChats();

  const { refreshChats } = useChatState();

  const {
    messageText,
    setMessageText,
    messageOrder,
    addToMessageOrder,
    removeFromMessageOrder,
    moveInMessageOrder,
    clearMessageOrder,
    sendMessages
  } = useMessageState();

  // Function to move items in message order

  const {
    pdfFiles,
    audioFile,
    imageFile,
    handlePdfUpload,
    handleAudioUpload,
    handleImageUpload,
    removePdfFile,
    removeAudioFile,
    removeImageFile
  } = useFileUpload();

  const {
    isSending,
    groupProgress,
    lastBroadcastChats,
    retryFailedAndPending,
    resetProgress
  } = useProgressState();

  const { showToast } = useToastState();

  const closeResultsModal = useCallback(() => {
    setShowResultsModal(false);
  }, []);

  // Função para obter ícone do tipo de elemento - memoized
  const getElementIcon = useCallback((type: string) => {
    switch (type) {
      case 'text': return '📝';
      case 'pdf': return '📄';
      case 'audio': return '🎵';
      case 'image': return '🖼️';
      default: return '📎';
    }
  }, []);

  // Função para obter nome do tipo de elemento - memoized
  const getElementName = useCallback((type: string) => {
    switch (type) {
      case 'text': return 'Texto';
      case 'pdf': return 'PDF';
      case 'audio': return 'Áudio';
      case 'image': return 'Imagem';
      default: return 'Arquivo';
    }
  }, []);

  // Memoize expensive calculations
  // Show Results card if we have any progress data OR a persisted broadcast snapshot
  // (the latter covers the case where the user refreshes mid-broadcast and we restored from localStorage)
  const hasResults = useMemo(() =>
    groupProgress.successfulGroups.length > 0
      || groupProgress.failedGroups.length > 0
      || (!isSending && lastBroadcastChats.length > 0),
    [groupProgress.successfulGroups.length, groupProgress.failedGroups.length, isSending, lastBroadcastChats.length]
  );

  // Groups that were targeted but didn't make it into successfulGroups (either explicitly failed or never attempted)
  const missingCount = useMemo(() =>
    Math.max(0, lastBroadcastChats.length - groupProgress.successfulGroups.length),
    [lastBroadcastChats.length, groupProgress.successfulGroups.length]
  );

  // After a refresh, File objects are lost. Warn if the persisted messageOrder needs files that aren't loaded.
  const needsFileReattach = useMemo(() => {
    if (!lastBroadcastChats.length) return false;
    return messageOrder.some(item =>
      (item.type === 'pdf' && !pdfFiles.some(p => p.id === item.fileId)) ||
      (item.type === 'audio' && !audioFile) ||
      (item.type === 'image' && !imageFile)
    );
  }, [lastBroadcastChats.length, messageOrder, pdfFiles, audioFile, imageFile]);

  const handleRetryFailedGroups = useCallback(() => {
    const n = retryFailedAndPending();
    if (n === 0) {
      showToast({
        type: 'info',
        title: 'Nenhum grupo faltante',
        message: 'Todos os grupos do último disparo já receberam a mensagem.'
      });
      return;
    }
    setShowResultsModal(false);
    showToast({
      type: 'info',
      title: 'Pronto para reenviar',
      message: `${n} grupo(s) preenchidos. ${needsFileReattach ? 'Reanexe os arquivos e ' : ''}clique em Enviar para continuar.`,
      duration: 6000
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [retryFailedAndPending, showToast, needsFileReattach]);

  const handleClearResults = useCallback(() => {
    resetProgress();
  }, [resetProgress]);

  // Additional handlers for simplified interface
  const handleChatToggle = useCallback((chatId: string) => {
    handleChatSelection(chatId);
  }, [handleChatSelection]);

  const clearSelection = useCallback(() => {
    handleClearSelection();
  }, [handleClearSelection]);

  const handleSendMessage = useCallback(() => {
    if (selectedChats.length > 0 && messageOrder.length > 0) {
      sendMessages();
    }
  }, [selectedChats.length, messageOrder.length, sendMessages]);

  // Progress tracking
  const currentStep = groupProgress.successfulGroups.length + groupProgress.failedGroups.length + 1;
  const currentGroup = isSending ? (selectedChats[currentStep - 1] || '') : '';
  const progress = selectedChats.length > 0 ? ((currentStep - 1) / selectedChats.length) * 100 : 0;

  return (
    <div className="wa-chat-workspace wa-animate-fade-in wa-theme-groups">
      {/* Header com título e ações */}
      <header className="wa-workspace-header">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="wa-heading-3" style={{ color: 'var(--wa-gray-900)', marginBottom: 'var(--wa-space-1)' }}>
              💬 Envio para Grupos
            </h1>
            <p style={{ fontSize: 'var(--wa-text-sm)', color: 'var(--wa-gray-600)' }}>
              Selecione grupos e monte sua mensagem para envio
            </p>
          </div>
          <div style={{ display: 'flex', gap: 'var(--wa-space-2)' }}>
            <button 
              onClick={handleRefresh} 
              title="Atualizar lista de grupos" 
              disabled={isLoading}
              className="wa-btn"
              style={{
                background: isLoading 
                  ? 'var(--wa-gray-400)' 
                  : 'var(--wa-gradient-yellow-primary)',
                color: isLoading ? 'white' : 'var(--wa-yellow-900)',
                border: isLoading ? 'none' : '2px solid var(--wa-yellow-400)',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--wa-space-2)',
                fontSize: 'var(--wa-text-sm)',
                fontWeight: 'var(--wa-font-semibold)'
              }}
            >
              <span style={{ fontSize: 'var(--wa-text-lg)' }}>🔄</span>
              {isLoading ? 'Carregando...' : 'Atualizar Grupos'}
            </button>
            
            {/* Force load button if no groups and not loading */}
            {allChats.length === 0 && !isLoading && (
              <button 
                onClick={() => refreshChats(true)} 
                title="Forçar carregamento mesmo sem conexão confirmada" 
                className="wa-btn wa-btn-outline"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--wa-space-2)',
                  fontSize: 'var(--wa-text-sm)',
                  fontWeight: 'var(--wa-font-semibold)'
                }}
              >
                <span style={{ fontSize: 'var(--wa-text-lg)' }}>🚀</span>
                Forçar Carregamento
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Workspace - Simplified Layout */}
      <div className="wa-workspace-simple">
        {/* Group Selection Section */}
        <div className="wa-section-card wa-animate-slide-up" style={{ animationDelay: '100ms' }}>
          <div className="wa-section-header">
            <h3 className="wa-panel-title">
              <span style={{ fontSize: 'var(--wa-text-lg)' }}>👥</span>
              <span>1. Selecionar Grupos</span>
              <span className="wa-counter">({selectedChats.length}/{allChats.length})</span>
            </h3>
            <p className="wa-section-instructions">
              👆 <strong>Clique nos grupos</strong> abaixo para selecioná-los para o envio
            </p>
          </div>
          
          {/* Enhanced Search and Filters */}
          <div className="wa-search-compact">
            <div className="relative flex-1">
              <div className="wa-input-with-icon">
                <span className="wa-input-icon">🔍</span>
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="wa-input wa-input-with-left-icon"
                  placeholder="⌨️ Digite para buscar grupos pelo nome... (ex: 'consultoria')"
                  style={{ width: '100%' }}
                />
                {searchQuery && (
                  <button
                    onClick={() => handleSearch('')}
                    className="wa-input-clear-btn"
                    title="Limpar busca"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            
            {/* Select All Filtered Button */}
            {searchQuery && filteredChats.length > 0 && (
              <button
                onClick={handleToggleAllFiltered}
                className={`wa-btn wa-btn-action ${areAllFilteredSelected ? 'wa-btn-selected' : 'wa-btn-outline'}`}
                title={areAllFilteredSelected ? 'Desmarcar todos os filtrados' : 'Marcar todos os filtrados'}
                style={{
                  fontSize: 'var(--wa-text-sm)',
                  fontWeight: 'var(--wa-font-semibold)',
                  whiteSpace: 'nowrap'
                }}
              >
                <span>{areAllFilteredSelected ? '☑️' : '📋'}</span>
                <span>{areAllFilteredSelected ? 'Desmarcar' : 'Marcar'} Todos ({filteredChats.length})</span>
              </button>
            )}
            
            <div className="wa-filters-compact">
              <button
                className={`wa-filter-chip ${activeFilters.status === 'all' ? 'active' : ''}`}
                onClick={() => setActiveFilters(prev => ({ ...prev, status: 'all' }))}
              >
                Todos ({allChats.length})
              </button>
              <button
                className={`wa-filter-chip ${activeFilters.status === 'active' ? 'active' : ''}`}
                onClick={() => setActiveFilters(prev => ({ ...prev, status: 'active' }))}
              >
                Ativos
              </button>
              <button
                className={`wa-filter-chip ${activeFilters.status === 'large' ? 'active' : ''}`}
                onClick={() => setActiveFilters(prev => ({ ...prev, status: 'large' }))}
              >
                Grandes
              </button>
            </div>
          </div>
          
          {/* Simplified Group List */}
          <div className="wa-groups-compact">
            {filteredChats.map((chat) => (
              <div 
                key={chat.id} 
                className={`wa-group-item-compact ${selectedChats.includes(chat.id) ? 'selected' : ''}`}
                onClick={() => handleChatToggle(chat.id)}
              >
                <div className={`wa-group-checkbox ${selectedChats.includes(chat.id) ? 'checked' : ''}`}>
                  {selectedChats.includes(chat.id) && '✓'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ 
                    fontWeight: 'var(--wa-font-semibold)', 
                    fontSize: 'var(--wa-text-sm)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {chat.subject || chat.id}
                  </div>
                  <div style={{ 
                    fontSize: 'var(--wa-text-xs)', 
                    color: 'var(--wa-gray-500)',
                    display: 'flex',
                    gap: 'var(--wa-space-2)'
                  }}>
                    <span>👥 Grupo</span>
                    <span>📊 Ativo</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {filteredChats.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: 'var(--wa-space-8)',
              color: 'var(--wa-gray-500)'
            }}>
              {searchQuery 
                ? `Nenhum grupo encontrado para "${searchQuery}"` 
                : 'Nenhum grupo disponível'
              }
            </div>
          )}
        </div>

        {/* Message Composer Section */}
        <div className="wa-section-card wa-animate-slide-up" style={{ animationDelay: '200ms' }}>
          <div className="wa-section-header">
            <h3 className="wa-panel-title">
              <span style={{ fontSize: 'var(--wa-text-lg)' }}>✏️</span>
              <span>2. Montar Mensagem</span>
            </h3>
            <p className="wa-section-instructions">
              📋 <strong>Organize a ordem</strong> dos elementos da sua mensagem arrastando-os na lista abaixo
            </p>
          </div>
          
          <div className="wa-composer-advanced">
            {/* Message Text Input */}
            <div className="wa-input-group">
              <label className="wa-element-label">
                <span>📝</span>
                <span>Texto da Mensagem</span>
              </label>
              <div className="wa-textarea-container">
                <textarea
                  className="wa-composer-textarea"
                  placeholder="📝 Digite sua mensagem aqui... 

💡 Dicas: 
• Use emojis 😊 para tornar a mensagem mais atrativa
• Pressione Enter para quebrar linhas
• Seja claro e objetivo na comunicação"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                />
                <div className="wa-textarea-footer">
                  <div className="wa-char-count">
                    <span className={messageText.length > 1600 ? 'wa-text-error' : 'wa-text-muted'}>
                      {messageText.length}/1600 caracteres
                    </span>
                  </div>
                </div>
              </div>
              {messageText.trim() && (
                <button
                  type="button"
                  onClick={() => addToMessageOrder({ type: 'text', content: messageText })}
                  className="wa-btn wa-btn-outline wa-btn-with-icon"
                  style={{ marginTop: 'var(--wa-space-2)' }}
                >
                  <span className="wa-btn-icon">➕</span>
                  <span>Adicionar Texto à Ordem</span>
                </button>
              )}
            </div>
            
            {/* File Upload Section */}
            <div className="wa-input-group">
              <label className="wa-element-label">
                <span>📎</span>
                <span>Anexos (PDFs, Áudios .ogg e Imagens)</span>
              </label>
              
              <div className="wa-file-upload-grid">
                {/* PDF Upload */}
                <div className="wa-file-upload-group">
                  <input
                    type="file"
                    id="pdf-upload"
                    accept=".pdf"
                    multiple
                    onChange={handlePdfUpload}
                    style={{ display: 'none' }}
                  />
                  <button 
                    type="button"
                    className="wa-btn wa-btn-outline wa-btn-with-icon"
                    onClick={() => document.getElementById('pdf-upload')?.click()}
                    title="Clique para anexar arquivos PDF (múltiplos permitidos)"
                  >
                    <span className="wa-btn-icon">📄</span>
                    <span>Adicionar PDF</span>
                  </button>
                </div>

                {/* Audio Upload */}
                <div className="wa-file-upload-group">
                  <input
                    type="file"
                    id="audio-upload"
                    accept=".ogg,audio/ogg"
                    multiple
                    onChange={handleAudioUpload}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="wa-btn wa-btn-outline wa-btn-with-icon"
                    onClick={() => document.getElementById('audio-upload')?.click()}
                    title="Clique para anexar áudios .ogg (múltiplos permitidos)"
                  >
                    <span className="wa-btn-icon">🎵</span>
                    <span>Adicionar Áudio</span>
                  </button>
                </div>

                {/* Image Upload */}
                <div className="wa-file-upload-group">
                  <input
                    type="file"
                    id="image-upload"
                    accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="wa-btn wa-btn-outline wa-btn-with-icon"
                    onClick={() => document.getElementById('image-upload')?.click()}
                    title="Clique para anexar imagens PNG, JPG ou WebP"
                  >
                    <span className="wa-btn-icon">🖼️</span>
                    <span>Adicionar Imagem</span>
                  </button>
                </div>
              </div>

              {/* File Preview and Add to Order */}
              {(pdfFiles.length > 0 || audioFile || imageFile) && (
                <div className="wa-upload-summary">
                  <div className="wa-upload-tips">
                    <span className="wa-tips-icon">💡</span>
                    <div className="wa-tips-content">
                      {pdfFiles.map(({ id, file }) => (
                        <div className="wa-file-info" key={id}>
                          <span className="wa-file-icon">📄</span>
                          <span className="wa-file-name">{file.name}</span>
                          <span className="wa-file-size">({Math.round(file.size / 1024)} KB)</span>
                          <div style={{ display: 'flex', gap: 'var(--wa-space-1)', marginTop: 'var(--wa-space-1)' }}>
                            <button
                              type="button"
                              onClick={() => addToMessageOrder({ type: 'pdf', content: file.name, fileId: id })}
                              className="wa-btn wa-btn-outline wa-btn-small"
                              title="Adicionar PDF à ordem de mensagem"
                            >
                              ➕ Adicionar à Ordem
                            </button>
                            <button
                              type="button"
                              onClick={() => removePdfFile(id)}
                              className="wa-btn wa-btn-small wa-btn-ghost"
                              title="Remover PDF"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ))}
                      {audioFile && (
                        <div className="wa-file-info">
                          <span className="wa-file-icon">🎵</span>
                          <span className="wa-file-name">{audioFile.name}</span>
                          <span className="wa-file-size">({Math.round(audioFile.size / 1024)} KB)</span>
                          <div style={{ display: 'flex', gap: 'var(--wa-space-1)', marginTop: 'var(--wa-space-1)' }}>
                            <button
                              type="button"
                              onClick={() => addToMessageOrder({ type: 'audio', content: audioFile.name })}
                              className="wa-btn wa-btn-outline wa-btn-small"
                              title="Adicionar áudio à ordem de mensagem"
                            >
                              ➕ Adicionar à Ordem
                            </button>
                            <button
                              onClick={removeAudioFile}
                              className="wa-btn wa-btn-small wa-btn-ghost"
                              title="Remover áudio"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      )}
                      {imageFile && (
                        <div className="wa-file-info">
                          <span className="wa-file-icon">🖼️</span>
                          <span className="wa-file-name">{imageFile.name}</span>
                          <span className="wa-file-size">({Math.round(imageFile.size / 1024)} KB)</span>
                          <div style={{ display: 'flex', gap: 'var(--wa-space-1)', marginTop: 'var(--wa-space-1)', flexWrap: 'wrap', alignItems: 'center' }}>
                            <input
                              type="text"
                              id="image-caption"
                              placeholder="Legenda (opcional)"
                              className="wa-input"
                              style={{ flex: '1 1 150px', maxWidth: '200px', fontSize: 'var(--wa-text-xs)', padding: 'var(--wa-space-1) var(--wa-space-2)' }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const captionInput = document.getElementById('image-caption') as HTMLInputElement;
                                const caption = captionInput?.value || '';
                                addToMessageOrder({ type: 'image', content: caption });
                              }}
                              className="wa-btn wa-btn-outline wa-btn-small"
                              title="Adicionar imagem à ordem de mensagem"
                            >
                              ➕ Adicionar à Ordem
                            </button>
                            <button
                              onClick={removeImageFile}
                              className="wa-btn wa-btn-small wa-btn-ghost"
                              title="Remover imagem"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Message Order Panel */}
            {messageOrder.length > 0 && (
              <div className="wa-input-group">
                <label className="wa-element-label">
                  <span>📋</span>
                  <span>Ordem dos Elementos da Mensagem</span>
                </label>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--wa-space-2)',
                  padding: 'var(--wa-space-3)',
                  background: 'var(--wa-gray-50)',
                  borderRadius: 'var(--wa-rounded-md)',
                  border: '1px solid var(--wa-gray-200)'
                }}>
                  {messageOrder.map((item, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--wa-space-2)',
                      padding: 'var(--wa-space-3)',
                      background: 'white',
                      borderRadius: 'var(--wa-rounded-sm)',
                      border: '1px solid var(--wa-gray-200)',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}>
                      {/* Order number */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 'var(--wa-space-6)',
                        height: 'var(--wa-space-6)',
                        background: 'var(--wa-primary-100)',
                        color: 'var(--wa-primary-800)',
                        borderRadius: 'var(--wa-rounded-full)',
                        fontSize: 'var(--wa-text-xs)',
                        fontWeight: 'var(--wa-font-bold)'
                      }}>
                        {index + 1}
                      </div>

                      {/* Item icon and content */}
                      <span style={{ fontSize: 'var(--wa-text-lg)' }}>{getElementIcon(item.type)}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          fontSize: 'var(--wa-text-sm)', 
                          fontWeight: 'var(--wa-font-semibold)',
                          color: 'var(--wa-gray-800)'
                        }}>
                          {getElementName(item.type)}
                        </div>
                        <div style={{ 
                          fontSize: 'var(--wa-text-xs)', 
                          color: 'var(--wa-gray-500)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {item.content.length > 40 ? item.content.substring(0, 40) + '...' : item.content}
                        </div>
                      </div>

                      {/* Move up/down buttons */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--wa-space-1)' }}>
                        <button
                          onClick={() => moveInMessageOrder(index, index - 1)}
                          disabled={index === 0}
                          className="wa-btn wa-btn-small wa-btn-ghost"
                          title="Mover para cima"
                          style={{ 
                            opacity: index === 0 ? 0.3 : 1,
                            cursor: index === 0 ? 'not-allowed' : 'pointer'
                          }}
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => moveInMessageOrder(index, index + 1)}
                          disabled={index === messageOrder.length - 1}
                          className="wa-btn wa-btn-small wa-btn-ghost"
                          title="Mover para baixo"
                          style={{ 
                            opacity: index === messageOrder.length - 1 ? 0.3 : 1,
                            cursor: index === messageOrder.length - 1 ? 'not-allowed' : 'pointer'
                          }}
                        >
                          ↓
                        </button>
                      </div>

                      {/* Remove button */}
                      <button
                        onClick={() => removeFromMessageOrder(index)}
                        className="wa-btn wa-btn-small wa-btn-ghost"
                        title="Remover item"
                        style={{ color: 'var(--wa-error-600)' }}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={clearMessageOrder}
                  className="wa-btn wa-btn-ghost wa-btn-small"
                  style={{ marginTop: 'var(--wa-space-2)' }}
                >
                  🗑️ Limpar Ordem
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Action Bar */}
        <div className="wa-action-bar wa-animate-slide-up" style={{ animationDelay: '300ms' }}>
          <div className="wa-action-header">
            <span className="wa-step-number">3.</span>
            <span className="wa-step-title">Confirmar e Enviar</span>
          </div>
          
          <div className="wa-action-content">
            <div className="wa-selection-summary">
              {selectedChats.length === 0 ? (
                <div className="wa-warning-message">
                  ⚠️ <strong>Selecione pelo menos um grupo</strong> para continuar
                </div>
              ) : (
                <div className="wa-success-message">
                  ✅ <strong>{selectedChats.length} grupos selecionados</strong>
                  {selectedChats.length > 0 && (
                    <button
                      onClick={clearSelection}
                      className="wa-link-button"
                      title="Clique para desmarcar todos os grupos"
                    >
                      Limpar seleção
                    </button>
                  )}
                </div>
              )}
              
              {messageOrder.length === 0 && selectedChats.length > 0 && (
                <div className="wa-warning-message">
                  ⚠️ <strong>Adicione pelo menos um elemento</strong> (texto, PDF, áudio ou imagem) à ordem de envio
                </div>
              )}

              {messageOrder.length > 0 && (
                <div className="wa-success-message">
                  📋 <strong>{messageOrder.length} elementos na ordem de envio</strong>
                </div>
              )}
            </div>
            
            <button
              className={`wa-send-button ${selectedChats.length === 0 || messageOrder.length === 0 ? 'disabled' : 'ready'}`}
              disabled={selectedChats.length === 0 || messageOrder.length === 0}
              onClick={handleSendMessage}
              title={selectedChats.length === 0 || messageOrder.length === 0
                ? 'Selecione grupos e adicione elementos à ordem de envio' 
                : 'Clique para enviar a mensagem para os grupos selecionados'
              }
            >
              <span className="wa-btn-icon">🚀</span>
              <span className="wa-btn-text">
                {selectedChats.length === 0 || messageOrder.length === 0
                  ? 'Preencha os campos acima' 
                  : `Enviar para ${selectedChats.length} grupos`
                }
              </span>
            </button>
          </div>
        </div>

        {/* Progress Display - Only show when sending */}
        {isSending && (
          <div className="wa-section-card">
            <h3 className="wa-panel-title">
              <span style={{ fontSize: 'var(--wa-text-lg)' }}>📊</span>
              Progresso do Envio
            </h3>
            
            <div className="wa-progress-container">
              <div className="wa-progress-bar-wrapper">
                <div className="wa-progress-bar">
                  <div 
                    className="wa-progress-fill"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="wa-progress-text">
                  {progress}% ({currentStep} de {selectedChats.length})
                </div>
              </div>
              
              {currentGroup && (
                <div className="wa-progress-status">
                  📝 Enviando para: {getGroupName(currentGroup)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Results Display - Only show when there are results */}
        {hasResults && (
          <div className="wa-section-card">
            <h3 className="wa-panel-title">
              <span style={{ fontSize: 'var(--wa-text-lg)' }}>📊</span>
              Resultado do Envio
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--wa-space-4)' }}>
              {groupProgress.successfulGroups.length > 0 && (
                <div style={{
                  padding: 'var(--wa-space-4)',
                  background: 'var(--wa-success-50)',
                  borderRadius: 'var(--wa-rounded-lg)',
                  border: '1px solid var(--wa-success-200)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--wa-space-2)',
                    marginBottom: 'var(--wa-space-2)',
                    fontSize: 'var(--wa-text-sm)',
                    fontWeight: 'var(--wa-font-semibold)',
                    color: 'var(--wa-success-700)'
                  }}>
                    <span>✅</span>
                    Enviados com sucesso ({groupProgress.successfulGroups.length})
                  </div>
                </div>
              )}
              
              {groupProgress.failedGroups.length > 0 && (
                <div style={{
                  padding: 'var(--wa-space-4)',
                  background: 'var(--wa-error-50)',
                  borderRadius: 'var(--wa-rounded-lg)',
                  border: '1px solid var(--wa-error-200)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--wa-space-2)',
                    marginBottom: 'var(--wa-space-2)',
                    fontSize: 'var(--wa-text-sm)',
                    fontWeight: 'var(--wa-font-semibold)',
                    color: 'var(--wa-error-700)'
                  }}>
                    <span>❌</span>
                    Falharam ({groupProgress.failedGroups.length})
                  </div>
                  <div style={{ fontSize: 'var(--wa-text-xs)', color: 'var(--wa-error-600)' }}>
                    {groupProgress.failedGroups.slice(0, 3).map((fail, i) => (
                      <div key={i}>• {getGroupName(fail.group)}: {fail.error}</div>
                    ))}
                    {groupProgress.failedGroups.length > 3 && (
                      <div>... e mais {groupProgress.failedGroups.length - 3} grupos</div>
                    )}
                  </div>
                </div>
              )}

              {/* Retry action: re-fills selection with groups that didn't receive the message
                  (explicit failures + groups never attempted if the broadcast was interrupted) */}
              {!isSending && missingCount > 0 && (
                <div style={{
                  padding: 'var(--wa-space-4)',
                  background: 'var(--wa-warning-50, #fffbeb)',
                  borderRadius: 'var(--wa-rounded-lg)',
                  border: '1px solid var(--wa-warning-200, #fcd34d)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--wa-space-3)'
                }}>
                  <div style={{
                    fontSize: 'var(--wa-text-sm)',
                    fontWeight: 'var(--wa-font-semibold)',
                    color: 'var(--wa-warning-700, #b45309)'
                  }}>
                    🔁 {missingCount} grupo(s) ainda não receberam a mensagem
                    {groupProgress.failedGroups.length < missingCount && (
                      <span style={{ fontWeight: 'var(--wa-font-normal)' }}>
                        {' '}({missingCount - groupProgress.failedGroups.length} não foram tentados — disparo interrompido)
                      </span>
                    )}
                  </div>
                  {needsFileReattach && (
                    <div style={{ fontSize: 'var(--wa-text-xs)', color: 'var(--wa-warning-700, #b45309)' }}>
                      ⚠️ Reanexe os arquivos (PDF/áudio/imagem) antes de reenviar.
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 'var(--wa-space-2)', flexWrap: 'wrap' }}>
                    <button
                      onClick={handleRetryFailedGroups}
                      className="wa-btn"
                      style={{
                        background: 'var(--wa-gradient-yellow-primary, linear-gradient(135deg, #fbbf24, #f59e0b))',
                        color: 'var(--wa-yellow-900, #78350f)',
                        border: '2px solid var(--wa-yellow-400, #facc15)',
                        fontSize: 'var(--wa-text-sm)',
                        fontWeight: 'var(--wa-font-semibold)',
                        cursor: 'pointer'
                      }}
                      title="Preenche a seleção apenas com os grupos que ainda não receberam"
                    >
                      🔄 Reenviar para os {missingCount} grupos faltantes
                    </button>
                    <button
                      onClick={handleClearResults}
                      className="wa-btn wa-btn-outline"
                      style={{ fontSize: 'var(--wa-text-sm)', cursor: 'pointer' }}
                      title="Descartar o resultado do último disparo"
                    >
                      Limpar resultado
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal de resultados detalhados */}
      {showResultsModal && (
        <ResultsModal
          failedGroups={groupProgress.failedGroups}
          getGroupName={getGroupName}
          onClose={closeResultsModal}
          onRetry={missingCount > 0 ? handleRetryFailedGroups : undefined}
          missingCount={missingCount}
        />
      )}
    </div>
  );
};

export default ChatPageV2;
